// The aligner's shell, the Chrome (src/app/hooks.ts) the shell's menu bar, palette and shortcuts
// sheet read here: their state and the aligner's registry, over the editor that is open. The
// shell's Shell holds a project, so the aligner keeps this one. A command reads the editor through
// `a`, which the editor sets on every render.

import type { Platform } from '../../core/bindings.ts'
import { chordOf, displayChord } from '../../core/keymap.ts'
import { menuHost, menuLabelOf, menuModel, shownChord, type MenuHost, type MenuModel } from '../../core/menu.ts'
import type { PaletteEntry, Screen } from '../../core/palette.ts'
import { recentsFor, remember, scopeOf } from '../../core/recents.ts'
import type { Registry } from '../../core/registry.ts'
import type { Invocation } from '../../core/trainer.ts'
import { BAR, type AlignMenuId, type AlignMenus } from '../core/menu.ts'
import type { Aligner } from './commands.ts'

export interface ShellState {
  palette: boolean
  menu: AlignMenuId | null
  shortcuts: boolean
}

const RECENTS = 'interplae.align.recentCommands'

export function detectPlatform(): Platform {
  return /Mac|iPhone|iPad/.test(navigator.platform) ? 'mac' : 'windows'
}

export class Shell {
  readonly platform: Platform
  readonly menuHost: MenuHost<AlignMenus>
  /// The palette is only ever open over a session.
  readonly paletteScreen: Screen = 'project'
  state: ShellState = { palette: false, menu: null, shortcuts: false }
  a: Aligner
  private listeners = new Set<() => void>()
  private paletteInput: HTMLInputElement | null = null
  private paletteReturn: Element | null = null
  private origin: Element | null = null

  constructor(readonly registry: Registry<Shell, AlignMenus>, a: Aligner, platform: Platform = detectPlatform()) {
    this.a = a
    this.platform = platform
    this.menuHost = menuHost(registry.menus, platform, BAR)
  }

  readonly store = {
    subscribe: (listener: () => void) => {
      this.listeners.add(listener)
      return () => void this.listeners.delete(listener)
    },
    get: () => this.state,
  }

  set(patch: Partial<ShellState>): void {
    this.state = { ...this.state, ...patch }
    for (const listener of this.listeners) listener()
  }

  /// A command by id, as a button, a menu item or the palette runs it. Null when its `when` does
  /// not hold. Only the palette and a pointer make a command recent: a chord is already the fast
  /// way.
  command(id: string, via: Invocation = 'pointer'): Promise<unknown> | null {
    const asked = this.registry.get(id)
    const ran = this.registry.run(id, this)
    if (ran !== null && via !== 'keyboard' && asked?.palette && !('hidden' in asked.palette)) {
      void ran.then(() => remember(localStorage, RECENTS, scopeOf(null, null, this.paletteScreen), id), () => {})
    }
    return ran
  }

  /// A chord pressed outside a field: the command it runs now. A chord the aligner binds stays
  /// the page's while its command cannot run, so Cmd+S during a realign never opens the browser's
  /// Save panel; the status line says why nothing happened instead.
  dispatch(chord: string): boolean {
    const command = this.registry.match(chord, this.platform, this)
    if (command) {
      command.run(this)
      return true
    }
    if (this.registry.bound(chord, this.platform).length === 0) return false
    if (!this.a.idle) this.a.say('Still working on the last change.')
    return true
  }

  /// The chord a key event spells here, or null for typing.
  chordOf(e: KeyboardEvent): string | null {
    return chordOf(e, this.platform)
  }

  /// A command's chord as this platform writes it, whether or not it can run now.
  chord(id: string): string | null {
    const chord = this.registry.chord(id, this.platform)
    return chord && displayChord(chord, this.platform)
  }

  /// The chord beside a command on every surface: null where another item takes it now.
  shownChord(id: string): string | null {
    const chord = shownChord(this.registry, this, id, this.platform)
    return chord && displayChord(chord, this.platform)
  }

  // The palette

  togglePalette(): void {
    if (this.state.palette) {
      this.set({ palette: false })
      return
    }
    this.paletteReturn = document.activeElement
    this.paletteInput?.focus()
    this.set({ palette: true, menu: null, shortcuts: false })
  }

  /// The palette's input mounted or went. The shell also hands over what was typed before it
  /// mounted; here the input is in the page from the start, so there is never any.
  paletteMounted(input: HTMLInputElement | null): string {
    this.paletteInput = input
    return ''
  }

  /// The palette closed: the focus goes back where the chord found it.
  paletteClosed(): void {
    const back = this.paletteReturn
    this.paletteReturn = null
    if (back instanceof HTMLElement && back.isConnected) back.focus({ preventScroll: true })
  }

  paletteEntries(): PaletteEntry[] {
    return this.registry.entries(this, this.paletteScreen, c => menuLabelOf(c.menu, this.menuHost))
  }

  recentCommands(): string[] {
    return recentsFor(localStorage, RECENTS, scopeOf(null, null, this.paletteScreen))
  }

  // The menus

  menuModel(): MenuModel<AlignMenuId> {
    return menuModel(this.registry, this, this.platform, this.menuHost)
  }

  /// Opens a menu of the bar, or switches to another; the first open records where the focus
  /// was, so a command or Escape puts it back. The overlays are exclusive.
  openMenu(menu: AlignMenuId): void {
    if (this.state.menu === null) this.origin = document.activeElement
    this.set({ menu, palette: false, shortcuts: false })
  }

  closeMenu(restore = true): void {
    this.set({ menu: null })
    const back = this.origin
    this.origin = null
    if (restore && back instanceof HTMLElement && back.isConnected) back.focus({ preventScroll: true })
  }

  /// A menu item chosen: the menu closes, the focus goes back to where it was, and the command
  /// runs there.
  invokeMenu(id: string): Promise<unknown> | null {
    this.closeMenu()
    return this.command(id)
  }
}
